import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  CircularProgress,
  InputAdornment,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Divider,
  Collapse,
  IconButton,
  FormHelperText,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  ExpenseForm as ExpenseFormType,
  ExpenseCategory,
  Listing,
  RecurrenceConfig,
} from '@/types';
import { ExpensesService } from '@/utils/expenses-service';
import { ListingsService } from '@/utils/listings-service';
import { parseCurrencyAmount } from '@/utils/currency-utils';

interface ExpenseFormProps {
  onSave: (expenseData: ExpenseFormType) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<ExpenseFormType>;
  isLoading?: boolean;
  enableOfflineMode?: boolean;
}

const ExpenseForm: React.FC<ExpenseFormProps> = ({
  onSave,
  onCancel,
  initialData,
  isLoading = false,
  enableOfflineMode = false,
}) => {
  // Form state
  const [formData, setFormData] = useState<ExpenseFormType>({
    categoryId: '',
    amount: initialData?.amount || 0,
    currency: 'USD',
    description: '',
    date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    recurrenceType: 'one_time',
    ...initialData,
  });

  // UI state
  const [categories, setCategories] = useState<{
    predefined: ExpenseCategory[];
    custom: ExpenseCategory[];
  }>({ predefined: [], custom: [] });
  const [listings, setListings] = useState<Listing[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingListings, setLoadingListings] = useState(true);
  const [showRecurrenceConfig, setShowRecurrenceConfig] = useState(false);
  const [showCustomCategoryForm, setShowCustomCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Load categories and listings on component mount

  // Show recurrence config when recurrence type changes
  useEffect(() => {
    setShowRecurrenceConfig(formData.recurrenceType !== 'one_time');
    
    // Initialize recurrence config for recurring expenses
    if (formData.recurrenceType !== 'one_time' && !formData.recurrenceConfig) {
      const startDate = formData.date;
      const nextDueDate = ExpensesService.calculateNextDueDate('monthly', new Date(startDate)).toISOString().split('T')[0];
      
      setFormData(prev => ({
        ...prev,
        recurrenceConfig: {
          frequency: 'monthly',
          startDate: prev.date,
          nextDueDate,
        },
      }));
    }
  }, [formData.recurrenceType, formData.date, formData.recurrenceConfig]);

  const loadCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      setErrors([]); // Clear previous errors
      
      const categoriesData = await ExpensesService.getExpenseCategories();
      
      // Ensure we have arrays even if the response is malformed
      const predefined = Array.isArray(categoriesData.predefinedCategories) 
        ? categoriesData.predefinedCategories 
        : [];
      const custom = Array.isArray(categoriesData.customCategories) 
        ? categoriesData.customCategories 
        : [];
      
      setCategories({
        predefined,
        custom,
      });
      
      console.log('Categories set:', { predefined: predefined.length, custom: custom.length });
    } catch (error) {
      console.error('Failed to load categories:', error);
      setErrors(['Failed to load expense categories. Please try again.']);
    } finally {
      setLoadingCategories(false);
    }
  }, []);
  useEffect(() => {
    loadCategories();
    loadListings();
  }, [loadCategories]);

  const loadListings = async () => {
    try {
      setLoadingListings(true);
      const listingsData = await ListingsService.getListings();
      setListings(listingsData);
    } catch (error) {
      console.error('Failed to load listings:', error);
      setErrors(prev => [...prev, 'Failed to load properties']);
    } finally {
      setLoadingListings(false);
    }
  };

  const handleInputChange = (field: keyof ExpenseFormType, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleCategoryChange = useCallback((categoryId: string) => {
    // Find the selected category to verify it exists
    const allCategories = [...categories.predefined, ...categories.custom];
    const selectedCategory = allCategories.find(cat => cat.categoryId === categoryId);
    console.log('Selected category object:', selectedCategory);
    
    setFormData(prev => {
      const updated = {
        ...prev,
        categoryId: categoryId,
      };
      console.log('Updated form data:', updated);
      return updated;
    });
    
    // Clear errors when user selects category
    if (errors.length > 0) {
      setErrors([]);
    }
  }, [categories, errors.length]);

  const handleRecurrenceConfigChange = (field: keyof RecurrenceConfig, value: any) => {
    setFormData(prev => {
      const updatedConfig = {
        ...prev.recurrenceConfig,
        [field]: value,
      } as RecurrenceConfig;

      // Recalculate next due date when frequency or start date changes
      if (field === 'frequency' || field === 'startDate') {
        const startDate = field === 'startDate' ? value : updatedConfig.startDate;
        const frequency = field === 'frequency' ? value : updatedConfig.frequency;
        
        if (startDate && frequency) {
          updatedConfig.nextDueDate = ExpensesService.calculateNextDueDate(
            frequency,
            new Date(startDate)
          ).toISOString().split('T')[0];
        }
      }

      return {
        ...prev,
        recurrenceConfig: updatedConfig,
      };
    });
  };

  const handleCreateCustomCategory = useCallback(async () => {
    if (!newCategoryName.trim()) {
      setErrors(['Category name is required']);
      return;
    }

    try {
      setCreatingCategory(true);
      setErrors([]); // Clear any existing errors
      
      const newCategory = await ExpensesService.createExpenseCategory({
        name: newCategoryName.trim(),
      });
      
      // Ensure the new category has the required fields
      if (!newCategory || !newCategory.categoryId) {
        console.error('Invalid category response:', newCategory);
        throw new Error('Invalid category response: missing categoryId');
      }
      
      // Add to custom categories list
      setCategories(prev => {
        const updated = {
          ...prev,
          custom: [...prev.custom, newCategory],
        };
        return updated;
      });
      
      // Select the new category
      setFormData(prev => {
        const updated = {
          ...prev,
          categoryId: newCategory.categoryId,
        };
        console.log('Selected new category in form:', updated);
        return updated;
      });
      
      // Reset form
      setNewCategoryName('');
      setShowCustomCategoryForm(false);
    } catch (error) {
      console.error('Failed to create category:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setErrors([`Failed to create custom category: ${errorMessage}`]);
    } finally {
      setCreatingCategory(false);
    }
  }, [newCategoryName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const validation = ExpensesService.validateExpense(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    try {
      await onSave(formData);
    } catch (error) {
      console.error('Failed to save expense:', error);
      setErrors(['Failed to save expense. Please try again.']);
    }
  };

  const allCategories = [...categories.predefined, ...categories.custom];

  return (
    <Paper elevation={2} sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        {initialData ? 'Edit Expense' : 'Add New Expense'}
      </Typography>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Expense Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Expense Details
            </Typography>
          </Grid>

          {/* Property Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Property (Optional)</InputLabel>
              <Select
                value={formData.listingId || ''}
                onChange={(e) => handleInputChange('listingId', e.target.value || undefined)}
                label="Property (Optional)"
                disabled={loadingListings}
              >
                <MenuItem value="">
                  <em>No specific property</em>
                </MenuItem>
                {listings.map((listing) => (
                  <MenuItem key={listing.listingId} value={listing.listingId}>
                    {listing.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Category Selection - Simple HTML Select */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Category *
              </Typography>
              <select
                value={formData.categoryId || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  console.log('Simple select onChange:', value);
                  handleCategoryChange(value);
                }}
                disabled={loadingCategories}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '16px',
                  backgroundColor: loadingCategories ? '#f5f5f5' : 'white',
                  cursor: loadingCategories ? 'not-allowed' : 'pointer'
                }}
                required
              >
                <option value="">
                  {loadingCategories ? 'Loading categories...' : 'Select a category'}
                </option>
                
                {!loadingCategories && categories.predefined.length > 0 && (
                  <optgroup label="Predefined Categories">
                    {categories.predefined.map((category) => (
                      <option key={`predefined-${category.categoryId}`} value={category.categoryId}>
                        {category.icon ? `${category.icon} ` : ''}{category.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                
                {!loadingCategories && categories.custom.length > 0 && (
                  <optgroup label="Custom Categories">
                    {categories.custom.map((category) => (
                      <option key={`custom-${category.categoryId}`} value={category.categoryId}>
                        {category.icon ? `${category.icon} ` : ''}{category.name} (Custom)
                      </option>
                    ))}
                  </optgroup>
                )}
                
                {!loadingCategories && categories.predefined.length === 0 && categories.custom.length === 0 && (
                  <option disabled>No categories available</option>
                )}
              </select>
              
              {!formData.categoryId && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                  Please select a category for this expense
                </Typography>
              )}
              
              {formData.categoryId && (
                <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                  Selected: {(() => {
                    const allCategories = [...categories.predefined, ...categories.custom];
                    const selected = allCategories.find(cat => cat.categoryId === formData.categoryId);
                    return selected ? selected.name : formData.categoryId;
                  })()}
                </Typography>
              )}
            </Box>
            
            {/* Category Actions */}
            <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setShowCustomCategoryForm(!showCustomCategoryForm)}
                disabled={creatingCategory}
              >
                Create Custom Category
              </Button>
              
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadCategories}
                disabled={loadingCategories}
              >
                Refresh
              </Button>
              
              <Typography variant="caption" color="textSecondary">
                ({categories.predefined.length + categories.custom.length} available)
              </Typography>
            </Box>
            
            <Collapse in={showCustomCategoryForm}>
              <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Create New Category
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs>
                    <TextField
                      fullWidth
                      size="small"
                      label="Category Name"
                      placeholder="e.g., Pool Maintenance, Guest Amenities"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      disabled={creatingCategory}
                      error={!newCategoryName.trim() && errors.some(e => e.includes('name'))}
                      helperText={!newCategoryName.trim() && errors.some(e => e.includes('name')) ? 'Category name is required' : ''}
                    />
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleCreateCustomCategory}
                      disabled={creatingCategory || !newCategoryName.trim()}
                      startIcon={creatingCategory ? <CircularProgress size={16} /> : <AddIcon />}
                    >
                      {creatingCategory ? 'Creating...' : 'Create'}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowCustomCategoryForm(false);
                        setNewCategoryName('');
                        setErrors(errors.filter(e => !e.includes('name')));
                      }}
                      disabled={creatingCategory}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Grid>

          {/* Amount and Currency */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="number"
              label="Amount"
              value={formData.amount || ''}
              onChange={(e) => {
                const value = e.target.value;
                const amount = parseCurrencyAmount(value);
                handleInputChange('amount', amount);
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                inputProps: { min: 0, step: 0.01 },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                onChange={(e) => handleInputChange('currency', e.target.value)}
                label="Currency"
              >
                <MenuItem value="USD">USD ($)</MenuItem>
                <MenuItem value="EUR">EUR (€)</MenuItem>
                <MenuItem value="GBP">GBP (£)</MenuItem>
                <MenuItem value="CAD">CAD (C$)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Date */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="date"
              label="Date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description (Optional)"
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Add any additional details about this expense..."
            />
          </Grid>

          {/* Recurrence Type */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <FormControl component="fieldset">
              <FormLabel component="legend">Expense Type</FormLabel>
              <RadioGroup
                row
                value={formData.recurrenceType}
                onChange={(e) => handleInputChange('recurrenceType', e.target.value)}
              >
                <FormControlLabel
                  value="one_time"
                  control={<Radio />}
                  label="One-time expense"
                />
                <FormControlLabel
                  value="fixed_recurring"
                  control={<Radio />}
                  label="Fixed recurring expense"
                />
                <FormControlLabel
                  value="variable_recurring"
                  control={<Radio />}
                  label="Variable recurring expense"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Recurrence Configuration */}
          <Grid item xs={12}>
            <Collapse in={showRecurrenceConfig}>
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Recurrence Settings
                  </Typography>
                  <IconButton
                    onClick={() => setShowRecurrenceConfig(!showRecurrenceConfig)}
                    sx={{ ml: 'auto' }}
                  >
                    {showRecurrenceConfig ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  {/* Frequency */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Frequency</InputLabel>
                      <Select
                        value={formData.recurrenceConfig?.frequency || 'monthly'}
                        onChange={(e) => handleRecurrenceConfigChange('frequency', e.target.value)}
                        label="Frequency"
                      >
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="quarterly">Quarterly</MenuItem>
                        <MenuItem value="yearly">Yearly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Start Date */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      value={formData.recurrenceConfig?.startDate || formData.date}
                      onChange={(e) => handleRecurrenceConfigChange('startDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {/* End Date (Optional) */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="End Date (Optional)"
                      value={formData.recurrenceConfig?.endDate || ''}
                      onChange={(e) => handleRecurrenceConfigChange('endDate', e.target.value || undefined)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {/* Fixed Amount Configuration */}
                  {formData.recurrenceType === 'fixed_recurring' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Recurring Amount"
                        value={formData.recurrenceConfig?.amount || formData.amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          const amount = parseCurrencyAmount(value);
                          handleRecurrenceConfigChange('amount', amount);
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          inputProps: { min: 0, step: 0.01 },
                        }}
                      />
                    </Grid>
                  )}

                  {/* Variable Amount Configuration */}
                  {formData.recurrenceType === 'variable_recurring' && (
                    <>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Min Amount"
                          value={formData.recurrenceConfig?.estimatedAmountRange?.min || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            const max = formData.recurrenceConfig?.estimatedAmountRange?.max || 0;
                            const min = parseCurrencyAmount(value);
                            handleRecurrenceConfigChange('estimatedAmountRange', { min, max });
                          }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputProps: { min: 0, step: 0.01 },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Max Amount"
                          value={formData.recurrenceConfig?.estimatedAmountRange?.max || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            const min = formData.recurrenceConfig?.estimatedAmountRange?.min || 0;
                            const max = parseCurrencyAmount(value);
                            handleRecurrenceConfigChange('estimatedAmountRange', { min, max });
                          }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputProps: { min: 0, step: 0.01 },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Reminder Days Before"
                          value={formData.recurrenceConfig?.reminderDaysBefore || 3}
                          onChange={(e) => handleRecurrenceConfigChange('reminderDaysBefore', parseInt(e.target.value) || 3)}
                          InputProps={{
                            inputProps: { min: 1, max: 30 },
                          }}
                          helperText="Days before due date to send reminder"
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Collapse>
          </Grid>

          {/* Form Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={isLoading || loadingCategories || loadingListings}
              >
                {isLoading ? 'Saving...' : 'Save Expense'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default ExpenseForm;